import { useState, useRef, useCallback } from 'react'
import { useLocation, useNavigate, Link } from 'react-router-dom'
import {
  Search, Bell, ChevronDown, LogOut, Settings, User, ChevronRight,
  CheckSquare, FileText, FolderOpen, CalendarDays, Megaphone, Menu,
  BellOff, HelpCircle, Activity, Loader2,
} from 'lucide-react'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '@/stores/authStore'
import { useUIStore } from '@/stores/uiStore'
import { useInstanceStore, isInstanceComplete } from '@/stores/instanceStore'
import { useSocketEvent } from '@/hooks/useSocket'
import { apiClient } from '@/services/api'
import { cn } from '@/lib/utils'

// Map path segments to breadcrumb labels
const BREADCRUMB_LABELS: Record<string, string> = {
  '': 'Dashboard',
  clients: 'Clienti',
  calendar: 'Calendario',
  projects: 'Progetti',
  tasks: 'Task',
  ai: 'AI Assistant',
  settings: 'Impostazioni',
  ped: 'Piano Editoriale',
  adv: 'ADV',
  'crescita-social': 'Crescita Social',
  events: 'Eventi',
  pr: 'Ufficio Stampa',
  branding: 'Branding',
  consulenza: 'Consulenza',
  digital: 'Digital Hub',
  reports: 'Report',
  notifications: 'Notifiche',
  users: 'Utenti',
  audit: 'Audit Trail',
}

interface BreadcrumbItem {
  label: string
  href?: string
}

function buildBreadcrumbs(pathname: string, rootLabel: string): BreadcrumbItem[] {
  const segments = pathname.split('/').filter(Boolean)
  const crumbs: BreadcrumbItem[] = [{ label: rootLabel, href: '/' }]

  let currentPath = ''
  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i] ?? ''
    currentPath += `/${seg}`
    const label = BREADCRUMB_LABELS[seg] ?? seg
    const isLast = i === segments.length - 1
    if (isLast) {
      crumbs.push({ label })
    } else {
      crumbs.push({ label, href: currentPath })
    }
  }

  if (segments.length === 0) {
    crumbs.push({ label: 'Dashboard' })
  }

  return crumbs
}

/** Returns a human-readable time-ago string in Italian */
function timeAgo(dateStr: string): string {
  const now = Date.now()
  const then = new Date(dateStr).getTime()
  const diffMs = now - then
  const diffSec = Math.floor(diffMs / 1000)
  const diffMin = Math.floor(diffSec / 60)
  const diffHour = Math.floor(diffMin / 60)
  const diffDay = Math.floor(diffHour / 24)

  if (diffSec < 60) return 'adesso'
  if (diffMin < 60) return `${diffMin} min fa`
  if (diffHour < 24) return `${diffHour} ${diffHour === 1 ? 'ora' : 'ore'} fa`
  if (diffDay === 1) return 'ieri'
  if (diffDay < 7) return `${diffDay} giorni fa`
  return new Date(dateStr).toLocaleDateString('it-IT', { day: '2-digit', month: 'short' })
}

/** Returns the notification link based on type and related entity */
function notificationLink(n: any): string {
  const type: string = (n.type ?? '').toUpperCase()
  if (type.startsWith('TASK')) return '/tasks'
  if (type.startsWith('PROJECT') && (n.entityId || n.relatedId)) return `/projects/${n.entityId || n.relatedId}`
  if (type.startsWith('CLIENT') && n.relatedId) return `/clients/${n.relatedId}`
  if (type.startsWith('EVENT') && n.relatedId) return `/events/${n.relatedId}`
  if (type.startsWith('CONTENT')) {
    const params = new URLSearchParams()
    if (n.relatedId) params.set('projectId', n.relatedId)
    if (n.entityId) params.set('contentId', n.entityId)
    const qs = params.toString()
    return qs ? `/ped?${qs}` : '/ped'
  }
  if (type.startsWith('QUOTE') || type.startsWith('PREVENTIV')) return '/preventivi'
  if (type.startsWith('MEETING')) return '/meetings'
  return '/notifications'
}

/** Returns an appropriate icon component for a notification type */
function NotificationIcon({ type }: { type: string }) {
  const t = (type ?? '').toUpperCase()
  if (t.startsWith('TASK')) return <CheckSquare className="w-3.5 h-3.5" />
  if (t.startsWith('PROJECT')) return <FolderOpen className="w-3.5 h-3.5" />
  if (t.startsWith('EVENT')) return <CalendarDays className="w-3.5 h-3.5" />
  if (t.startsWith('CAMPAIGN') || t.startsWith('ADV')) return <Megaphone className="w-3.5 h-3.5" />
  if (t.startsWith('CONTENT')) return <FileText className="w-3.5 h-3.5" />
  return <FileText className="w-3.5 h-3.5" />
}

/** Urgency-based background color for notification icon */
/** Resolve effective notification type using title as fallback for legacy CONTENT_REVIEW entries */
function resolveNotifType(type: string, title?: string): string {
  const t = (type ?? '').toUpperCase()
  // Legacy: CONTENT_REVIEW with title "Contenuto pronto" → treat as CONTENT_READY
  if (t === 'CONTENT_REVIEW' && title && /pronto/i.test(title) && !/approva|revision/i.test(title)) {
    return 'CONTENT_READY'
  }
  return t
}

function notifIconBg(type: string, title?: string): string {
  const t = resolveNotifType(type, title)
  // Critical — requires immediate action (danger)
  if (t === 'CONTENT_CHANGES_REQUESTED') return 'bg-danger-50 text-danger-600'
  if (t === 'TASK_REOPENED') return 'bg-danger-50 text-danger-600'
  if (t === 'PENDING_CHANGE_REQUEST') return 'bg-danger-50 text-danger-600'
  // Warning — action needed (warning)
  if (t === 'TASK_ASSIGNED' || t === 'TASK_REASSIGNED' || t === 'TASK_ASSIGNED_NOTIFY') return 'bg-warning-50 text-warning-600'
  if (t === 'CONTENT_REVIEW' || t === 'CONTENT_REWORKED') return 'bg-warning-50 text-warning-600'
  // Success — positive / informational (success)
  if (t === 'CONTENT_READY') return 'bg-success-50 text-success-600'
  if (t === 'CONTENT_APPROVED') return 'bg-success-50 text-success-600'
  if (t === 'PENDING_CHANGE_APPROVED') return 'bg-success-50 text-success-600'
  if (t === 'TASK_STATUS' || t === 'TASK_STATUS_CHANGE') return 'bg-success-50 text-success-600'
  // Rejected (danger)
  if (t === 'PENDING_CHANGE_REJECTED') return 'bg-danger-50 text-danger-600'
  // Comments — high priority (orange, category color)
  if (t === 'PROJECT_COMMENT' || t === 'TASK_COMMENT' || t === 'TASK_COMMENT_NOTIFY' || t === 'CONTENT_COMMENT') return 'bg-orange-100 text-orange-600'
  // Info — general updates (entity-category colors)
  if (t.startsWith('PROJECT')) return 'bg-purple-100 text-purple-600'
  if (t.startsWith('CONTENT')) return 'bg-primary-50 text-primary-600'
  if (t.startsWith('TASK')) return 'bg-slate-100 text-slate-600'
  if (t.startsWith('EVENT')) return 'bg-pink-100 text-pink-600'
  if (t.startsWith('CAMPAIGN') || t.startsWith('ADV')) return 'bg-orange-100 text-orange-600'
  return 'bg-primary-50 text-primary-600'
}

/** Unread dot color — red for critical/warning, blue for the rest */
function notifDotColor(type: string, title?: string): string {
  const t = resolveNotifType(type, title)
  if (t === 'CONTENT_CHANGES_REQUESTED' || t === 'TASK_REOPENED' || t === 'PENDING_CHANGE_REQUEST') return 'bg-danger-500'
  if (t === 'TASK_ASSIGNED' || t === 'TASK_REASSIGNED' || t === 'TASK_ASSIGNED_NOTIFY' || t === 'CONTENT_REVIEW' || t === 'CONTENT_REWORKED') return 'bg-danger-500'
  if (t === 'PENDING_CHANGE_REJECTED') return 'bg-danger-500'
  if (t === 'PROJECT_COMMENT' || t === 'TASK_COMMENT' || t === 'TASK_COMMENT_NOTIFY' || t === 'CONTENT_COMMENT') return 'bg-orange-500'
  return 'bg-primary-500'
}

/** Left border color for urgency in dropdown */
function notifBorderLeft(type: string, title?: string): string {
  const t = resolveNotifType(type, title)
  if (t === 'CONTENT_CHANGES_REQUESTED' || t === 'TASK_REOPENED' || t === 'PENDING_CHANGE_REQUEST') return 'border-l-4 border-l-danger-500'
  if (t === 'TASK_ASSIGNED' || t === 'TASK_REASSIGNED' || t === 'TASK_ASSIGNED_NOTIFY' || t === 'CONTENT_REVIEW' || t === 'CONTENT_REWORKED') return 'border-l-4 border-l-warning-500'
  if (t === 'CONTENT_READY' || t === 'CONTENT_APPROVED' || t === 'PENDING_CHANGE_APPROVED' || t === 'TASK_STATUS' || t === 'TASK_STATUS_CHANGE') return 'border-l-4 border-l-success-500'
  if (t === 'PENDING_CHANGE_REJECTED') return 'border-l-4 border-l-danger-500'
  if (t === 'PROJECT_COMMENT' || t === 'TASK_COMMENT' || t === 'TASK_COMMENT_NOTIFY' || t === 'CONTENT_COMMENT') return 'border-l-4 border-l-orange-500'
  return 'border-l-4 border-l-primary-300'
}

// ─── Help content per page ───────────────────────────────────────────
interface HelpSection { heading: string; text: string }
interface HelpEntry { title: string; sections: HelpSection[] }

const HELP_CONTENT: Record<string, HelpEntry> = {
  dashboard: {
    title: 'Dashboard',
    sections: [
      { heading: 'Panoramica', text: 'La dashboard mostra un riepilogo dello stato di progetti, task, scadenze e attivitá recenti. I widget si aggiornano in tempo reale.' },
      { heading: 'Widget', text: 'Ogni card riassume una metrica chiave: task in scadenza, progetti attivi, notifiche non lette e ore lavorate.' },
      { heading: 'Azioni rapide', text: 'Usa i pulsanti in alto per creare velocemente un nuovo progetto, task o evento senza navigare altrove.' },
    ],
  },
  clients: {
    title: 'Clienti',
    sections: [
      { heading: 'Elenco clienti', text: 'Visualizza tutti i clienti con ricerca, filtri per stato e ordinamento. Clicca su un cliente per vedere il dettaglio.' },
      { heading: 'Scheda cliente', text: 'Contiene anagrafica, contatti, progetti collegati, preventivi e storico attivitá.' },
      { heading: 'Azioni', text: 'Puoi creare, modificare ed eliminare clienti. L\'eliminazione richiede conferma e permessi adeguati.' },
    ],
  },
  projects: {
    title: 'Progetti',
    sections: [
      { heading: 'Lista progetti', text: 'Tutti i progetti con filtri per stato, cliente e responsabile. Usa la barra di ricerca per trovare rapidamente un progetto.' },
      { heading: 'Dettaglio progetto', text: 'Mostra task, team, file, timeline e budget del progetto. Puoi aggiungere moduli (ADV, PR, Branding, ecc.) dal dettaglio.' },
      { heading: 'Moduli', text: 'Ogni progetto puó avere moduli specializzati che aggiungono funzionalitá specifiche come campagne ADV o attivitá PR.' },
    ],
  },
  tasks: {
    title: 'Task',
    sections: [
      { heading: 'Kanban', text: 'Visualizza i task in colonne per stato (Da fare, In corso, Completati). Trascina le card per cambiare stato.' },
      { heading: 'Filtri', text: 'Filtra per progetto, assegnatario, prioritá e scadenza. I filtri si combinano per ricerche precise.' },
      { heading: 'Dettaglio task', text: 'Ogni task ha descrizione, checklist, allegati, commenti e log delle attivitá. Puoi assegnare e cambiare prioritá.' },
    ],
  },
  calendar: {
    title: 'Calendario Personale',
    sections: [
      { heading: 'Vista calendario', text: 'Visualizza i tuoi impegni in vista giornaliera, settimanale o mensile. Gli eventi sono colorati per tipo.' },
      { heading: 'Creazione eventi', text: 'Clicca su un giorno o un\'ora per creare un nuovo evento. Puoi impostare titolo, orario, luogo e promemoria.' },
      { heading: 'Sincronizzazione', text: 'Gli eventi dei progetti e le scadenze dei task appaiono automaticamente nel calendario.' },
    ],
  },
  ped: {
    title: 'Piano Editoriale (PED)',
    sections: [
      { heading: 'Calendario editoriale', text: 'Pianifica i contenuti su un calendario visuale. Ogni card rappresenta un contenuto con stato, canale e data di pubblicazione.' },
      { heading: 'Contenuti', text: 'Crea contenuti con testo, immagini e video. Assegna canali social, categorie e responsabili.' },
      { heading: 'Workflow', text: 'I contenuti seguono un flusso: Bozza → In revisione → Approvato → Pubblicato. Ogni passaggio puó richiedere approvazione.' },
    ],
  },
  adv: {
    title: 'ADV — Campagne Pubblicitarie',
    sections: [
      { heading: 'Gestione campagne', text: 'Crea e monitora campagne pubblicitarie con budget, date, canali e KPI. Collega le campagne ai progetti cliente.' },
      { heading: 'Budget e spese', text: 'Traccia il budget allocato vs speso per ogni campagna. Visualizza il rendimento con grafici e metriche.' },
      { heading: 'Report', text: 'Genera report periodici sulle performance delle campagne con metriche chiave come impression, click e conversioni.' },
    ],
  },
  'crescita-social': {
    title: 'Crescita Social a pagamento',
    sections: [
      { heading: 'Listino', text: 'I servizi selezionati da E-quipe sul panel SMM, con nome italiano e ricarico. È da qui che si ordina di solito.' },
      { heading: 'Ordini', text: "Ogni ordine passa da un preventivo che mostra costo reale, prezzo al cliente e saldo residuo: la spesa parte solo dopo conferma esplicita." },
      { heading: 'Margine', text: 'Costo del panel e prezzo al cliente restano affiancati su ogni ordine, con il margine per cliente e per progetto.' },
    ],
  },
  pr: {
    title: 'Ufficio Stampa (PR)',
    sections: [
      { heading: 'Comunicati stampa', text: 'Gestisci comunicati stampa con workflow di approvazione. Traccia invii e copertura mediatica.' },
      { heading: 'Media list', text: 'Mantieni un database di contatti media con testate, giornalisti e informazioni di contatto.' },
      { heading: 'Rassegna stampa', text: 'Raccogli e organizza le uscite stampa collegate ai clienti e ai progetti.' },
    ],
  },
  branding: {
    title: 'Branding',
    sections: [
      { heading: 'Identitá visiva', text: 'Gestisci loghi, palette colori, font e linee guida del brand per ogni cliente.' },
      { heading: 'Asset', text: 'Carica e organizza gli asset grafici. Ogni file é versionato e collegato al progetto.' },
      { heading: 'Guideline', text: 'Crea documenti di brand guideline con regole d\'uso del marchio e template.' },
    ],
  },
  consulenza: {
    title: 'Consulenza',
    sections: [
      { heading: 'Attivitá di consulenza', text: 'Traccia le ore e le attivitá di consulenza per ogni cliente. Registra meeting, call e sessioni di lavoro.' },
      { heading: 'Report ore', text: 'Visualizza il riepilogo ore per cliente e periodo. Esporta i dati per la fatturazione.' },
    ],
  },
  digital: {
    title: 'Digital Hub',
    sections: [
      { heading: 'Panoramica', text: 'Hub centralizzato per le attivitá digitali: SEO, social media, content marketing e analytics.' },
      { heading: 'Monitoraggio', text: 'Controlla le metriche digitali dei clienti con grafici e KPI aggiornati.' },
      { heading: 'Attivitá', text: 'Gestisci le attivitá digital con task dedicati, scadenze e assegnatari.' },
    ],
  },
  'visual-production': {
    title: 'Visual Production',
    sections: [
      { heading: 'Produzioni', text: 'Gestisci shooting fotografici e video con brief, calendario, team e deliverable.' },
      { heading: 'Workflow', text: 'Ogni produzione segue fasi: Brief → Pre-produzione → Produzione → Post-produzione → Consegna.' },
      { heading: 'File e asset', text: 'Carica e organizza foto e video. I file sono collegati ai progetti e ai clienti.' },
    ],
  },
  email: {
    title: 'Email',
    sections: [
      { heading: 'Casella di posta', text: 'Gestisci le email direttamente dall\'ERP. Visualizza posta in arrivo, inviata e bozze.' },
      { heading: 'Composizione', text: 'Scrivi nuove email con editor rich text, allegati e firme personalizzate.' },
      { heading: 'Account', text: 'Collega uno o piú account email (IMAP/SMTP) per centralizzare la comunicazione.' },
    ],
  },
  meetings: {
    title: 'Meeting',
    sections: [
      { heading: 'Pianificazione', text: 'Crea e pianifica meeting con data, ora, partecipanti e ordine del giorno.' },
      { heading: 'Note e follow-up', text: 'Registra note durante il meeting e crea task di follow-up collegati automaticamente.' },
    ],
  },
  quotes: {
    title: 'Preventivi',
    sections: [
      { heading: 'Elenco preventivi', text: 'Visualizza tutti i preventivi con stato (bozza, inviato, approvato, rifiutato) e importo.' },
      { heading: 'Creazione', text: 'Crea preventivi con voci dettagliate, quantitá, prezzi e sconti. Collega il preventivo a un cliente e progetto.' },
      { heading: 'PDF e invio', text: 'Genera il PDF del preventivo e invialo direttamente via email al cliente.' },
    ],
  },
  settings: {
    title: 'Impostazioni',
    sections: [
      { heading: 'Profilo', text: 'Modifica i tuoi dati personali, email, password e preferenze di notifica.' },
      { heading: 'Configurazione', text: 'Gestisci le impostazioni generali dell\'applicazione come tema, lingua e fuso orario.' },
    ],
  },
  ai: {
    title: 'AI Assistant',
    sections: [
      { heading: 'Chat AI', text: 'Interagisci con l\'assistente AI per ottenere risposte su progetti, task e dati dell\'ERP.' },
      { heading: 'Azioni AI', text: 'L\'assistente puó creare task, riassumere progetti, analizzare dati e suggerire azioni basandosi sul contesto.' },
      { heading: 'Knowledge Base', text: 'L\'AI accede alla knowledge base aziendale per risposte piú accurate e contestualizzate.' },
    ],
  },
  knowledge: {
    title: 'Knowledge Base',
    sections: [
      { heading: 'Documenti', text: 'Carica documenti aziendali (PDF, Word, testo) che vengono indicizzati per la ricerca semantica.' },
      { heading: 'Ricerca', text: 'Cerca informazioni nei documenti caricati. I risultati sono ordinati per rilevanza.' },
      { heading: 'AI integrata', text: 'L\'AI Assistant usa la knowledge base per rispondere alle domande con informazioni aziendali verificate.' },
    ],
  },
  resources: {
    title: 'Risorse',
    sections: [
      { heading: 'Gestione risorse', text: 'Visualizza e gestisci le risorse aziendali: persone, attrezzature e spazi.' },
      { heading: 'Disponibilitá', text: 'Controlla la disponibilitá delle risorse e pianifica l\'allocazione sui progetti.' },
      { heading: 'Workload', text: 'Monitora il carico di lavoro di ogni risorsa per evitare sovraccarichi e ottimizzare la distribuzione.' },
    ],
  },
  social: {
    title: 'Social Publisher',
    sections: [
      { heading: 'Pubblicazione', text: 'Pianifica e pubblica contenuti sui canali social dei clienti da un\'unica interfaccia.' },
      { heading: 'Calendario', text: 'Visualizza il piano di pubblicazione su un calendario con vista settimanale e mensile.' },
      { heading: 'Analytics', text: 'Monitora le performance dei post pubblicati con metriche di engagement e reach.' },
    ],
  },
  suppliers: {
    title: 'Fornitori',
    sections: [
      { heading: 'Anagrafica', text: 'Gestisci l\'elenco fornitori con dati di contatto, categorie e condizioni commerciali.' },
      { heading: 'Ordini', text: 'Traccia ordini e forniture. Collega i fornitori ai progetti per monitorare i costi.' },
    ],
  },
  users: {
    title: 'Gestione Utenti',
    sections: [
      { heading: 'Utenti', text: 'Visualizza e gestisci gli utenti del sistema. Crea nuovi utenti e assegna ruoli e permessi.' },
      { heading: 'Ruoli', text: 'Il sistema ha 4 livelli: Super Admin (L1), Admin (L2), Manager (L3) e Operativo (L4). Ogni livello ha permessi specifici.' },
      { heading: 'Sicurezza', text: 'Gestisci password, sessioni attive e log di accesso degli utenti.' },
    ],
  },
  reports: {
    title: 'Report',
    sections: [
      { heading: 'Report disponibili', text: 'Genera report su progetti, task, ore lavorate, budget e performance. I dati sono filtrabili per periodo e cliente.' },
      { heading: 'Esportazione', text: 'Esporta i report in PDF o Excel per condividerli con il team o i clienti.' },
    ],
  },
  workload: {
    title: 'Workload',
    sections: [
      { heading: 'Carico di lavoro', text: 'Visualizza il carico di lavoro del team con grafici per persona, progetto e periodo.' },
      { heading: 'Bilanciamento', text: 'Identifica squilibri nel carico di lavoro e redistribuisci i task per ottimizzare la produttivitá.' },
    ],
  },
  accounting: {
    title: 'Contabilitá',
    sections: [
      { heading: 'Fatture', text: 'Gestisci fatture emesse e ricevute. Traccia lo stato dei pagamenti e le scadenze.' },
      { heading: 'Situazione', text: 'Panoramica finanziaria con entrate, uscite e saldo. Filtra per periodo e cliente.' },
      { heading: 'Spese', text: 'Registra e categorizza le spese aziendali. Collega le spese ai progetti per il controllo dei costi.' },
    ],
  },
  events: {
    title: 'Eventi',
    sections: [
      { heading: 'Gestione eventi', text: 'Pianifica e gestisci eventi aziendali e dei clienti con date, location, budget e partecipanti.' },
      { heading: 'Timeline', text: 'Visualizza la timeline dell\'evento con tutte le attivitá preparatorie e le scadenze.' },
    ],
  },
  notifications: {
    title: 'Notifiche',
    sections: [
      { heading: 'Centro notifiche', text: 'Tutte le notifiche in un\'unica pagina. Filtra per tipo, stato (lette/non lette) e periodo.' },
      { heading: 'Gestione', text: 'Segna le notifiche come lette singolarmente o tutte insieme. Le notifiche piú vecchie vengono archiviate automaticamente.' },
    ],
  },
}

function getHelpForPath(pathname: string): HelpEntry {
  const segments = pathname.split('/').filter(Boolean)
  const key = segments[0] ?? 'dashboard'
  return HELP_CONTENT[key] ?? HELP_CONTENT['dashboard']!
}

export function Header() {
  const location = useLocation()
  const navigate = useNavigate()
  const [searchValue, setSearchValue] = useState('')
  const [notifOpen, setNotifOpen] = useState(false)
  const [badgeDismissed, setBadgeDismissed] = useState(() => {
    const stored = sessionStorage.getItem('notif-badge-dismissed')
    return stored === 'true'
  })
  const [helpOpen, setHelpOpen] = useState(false)
  const [activityOpen, setActivityOpen] = useState(false)
  const [activityPage, setActivityPage] = useState(1)
  const [searchResults, setSearchResults] = useState<Record<string, any[]> | null>(null)
  const [isSearching, setIsSearching] = useState(false)
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const user = useAuthStore((state) => state.user)
  const logout = useAuthStore((state) => state.logout)
  const toggleMobileSidebar = useUIStore((state) => state.toggleMobileSidebar)
  const queryClient = useQueryClient()
  const instance = useInstanceStore((state) => state.instance)
  const instanceComplete = isInstanceComplete(instance)

  const breadcrumbs = buildBreadcrumbs(location.pathname, instance.name)
  const userInitials = user
    ? `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase()
    : 'U'

  // Unread notification count - refetched every 30s
  const { data: unreadData } = useQuery({
    queryKey: ['notifications-unread-count'],
    queryFn: () => apiClient.get<{ count: number }>('/notifications/unread-count'),
    enabled: !!user,
    refetchInterval: 30000,
    retry: false,
    placeholderData: { count: 0 },
  })
  const notificationCount = (unreadData as any)?.count ?? (unreadData as any)?.data?.count ?? 0

  // Aggiorna badge notifiche in tempo reale via Socket.IO
  useSocketEvent('notification:new', () => {
    setBadgeDismissed(false)
    sessionStorage.setItem('notif-badge-dismissed', 'false')
    void queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] })
    void queryClient.invalidateQueries({ queryKey: ['notifications-recent'] })

    // Suono notifica
    try {
      const ctx = new AudioContext()
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()
      osc.connect(gain)
      gain.connect(ctx.destination)
      osc.frequency.value = 880
      osc.type = 'sine'
      gain.gain.setValueAtTime(0.15, ctx.currentTime)
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3)
      osc.start(ctx.currentTime)
      osc.stop(ctx.currentTime + 0.3)
    } catch { /* audio non supportato */ }
  })

  // Recent notifications for the popover panel
  const { data: notificationsData } = useQuery({
    queryKey: ['notifications-recent'],
    queryFn: () => apiClient.get<{ data: any[] }>('/notifications', { limit: 10 } as Record<string, unknown>),
    enabled: !!user,
    refetchInterval: 30000,
    retry: false,
    placeholderData: { data: [] },
  })
  // Backend may return { data: [...] }, { data: { data: [...] } }, or [...] directly
  const rawNotif = notificationsData as any
  const notifications: any[] =
    Array.isArray(rawNotif) ? rawNotif :
    Array.isArray(rawNotif?.data) ? rawNotif.data :
    Array.isArray(rawNotif?.items) ? rawNotif.items :
    Array.isArray(rawNotif?.data?.data) ? rawNotif.data.data :
    []

  // Mark notifications as read
  const markReadMutation = useMutation({
    mutationFn: (data: { ids?: string[]; all?: boolean }) =>
      apiClient.post('/notifications/mark-read', data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] })
      void queryClient.invalidateQueries({ queryKey: ['notifications-recent'] })
    },
  })

  // Activity feed query
  const { data: activityData, isLoading: activityLoading } = useQuery({
    queryKey: ['activity-feed', activityPage],
    queryFn: () => apiClient.get<any>('/audit-log/feed', { page: activityPage, limit: 30 }),
    enabled: activityOpen,
    retry: false,
  })
  const activityRaw = activityData as any
  const activityItems: any[] = activityRaw?.data?.data ?? activityRaw?.data ?? []
  const activityPagination = activityRaw?.data?.pagination ?? activityRaw?.pagination ?? null

  // Translate action verbs to Italian
  const actionLabel = useCallback((action: string): { label: string; color: string } => {
    const a = action.toLowerCase()
    if (a.includes('creat') || a === 'create') return { label: 'ha creato', color: 'text-success-600' }
    if (a.includes('updat') || a === 'update') return { label: 'ha modificato', color: 'text-primary-600' }
    if (a.includes('delet') || a === 'delete') return { label: 'ha eliminato', color: 'text-danger-600' }
    if (a.includes('archiv')) return { label: 'ha archiviato', color: 'text-warning-600' }
    if (a.includes('restor')) return { label: 'ha ripristinato', color: 'text-success-600' }
    if (a.includes('assign')) return { label: 'ha assegnato', color: 'text-purple-600' }
    return { label: action, color: 'text-slate-600' }
  }, [])

  // Translate entity types to Italian
  const entityLabel = useCallback((entity: string): string => {
    const map: Record<string, string> = {
      project: 'progetto',
      client: 'cliente',
      task: 'task',
      content: 'contenuto',
      editorialplan: 'piano editoriale',
      adcampaign: 'campagna ADV',
      meeting: 'meeting',
      pressrelease: 'comunicato stampa',
      brandproject: 'progetto branding',
      consultingproject: 'consulenza',
      document: 'documento',
      ticket: 'ticket',
      expense: 'spesa',
      calendarevent: 'evento',
      contract: 'contratto',
      user: 'utente',
      contact: 'contatto',
      quote: 'preventivo',
      subscription: 'abbonamento',
      supplier: 'fornitore',
    }
    return map[entity.toLowerCase()] ?? entity
  }, [])

  // Navigate to entity detail page
  const entityRoute = useCallback((entity: string, entityId: string): string | null => {
    const routes: Record<string, string> = {
      project: `/projects/${entityId}`,
      client: `/clients/${entityId}`,
      task: '/tasks',
      content: `/ped?projectId=${entityId}`,
      editorialplan: `/ped?projectId=${entityId}`,
      adcampaign: '/adv',
      meeting: '/meetings',
      pressrelease: '/pr',
      brandproject: '/branding',
      consultingproject: '/consulenza',
      document: '/digital',
      quote: `/quotes/${entityId}`,
      supplier: '/suppliers',
      subscription: '/accounting',
      user: '/users',
      calendarevent: '/calendar',
      expense: '/accounting',
    }
    return routes[entity.toLowerCase()] ?? null
  }, [])

  // Format changes object for display
  const formatChanges = useCallback((changes: Record<string, unknown> | null): { key: string; value: string }[] => {
    if (!changes || typeof changes !== 'object') return []
    const skip = new Set(['id', 'createdAt', 'updatedAt', 'deletedAt', 'slug', 'createdById', 'userId'])
    const labelMap: Record<string, string> = {
      name: 'Nome', title: 'Titolo', status: 'Stato', description: 'Descrizione',
      email: 'Email', phone: 'Telefono', isActive: 'Attivo', priority: 'Priorità',
      dueDate: 'Scadenza', assigneeId: 'Assegnatario', clientId: 'Cliente',
      projectId: 'Progetto', budget: 'Budget', notes: 'Note', address: 'Indirizzo',
      website: 'Sito web', vatNumber: 'P.IVA', iban: 'IBAN', contactName: 'Referente',
      contactPhone: 'Tel. referente', contactEmail: 'Email referente',
      paymentTerms: 'Termini pagamento', cost: 'Costo', billingCycle: 'Ciclo fatturazione',
      quoteNumber: 'N. preventivo', issueDate: 'Data emissione', expiryDate: 'Scadenza',
      subtotal: 'Subtotale', taxTotal: 'IVA', discountTotal: 'Sconto totale',
    }
    return Object.entries(changes)
      .filter(([k]) => !skip.has(k))
      .slice(0, 8)
      .map(([k, v]) => ({
        key: labelMap[k] ?? k,
        value: v === true ? 'Sì' : v === false ? 'No' : v === null ? '—' : String(v).slice(0, 80),
      }))
  }, [])

  const [expandedActivityId, setExpandedActivityId] = useState<string | null>(null)

  // Debounced search handler
  function handleSearchChange(value: string) {
    setSearchValue(value)
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current)

    if (!value.trim() || value.trim().length < 2) {
      setSearchResults(null)
      return
    }

    searchTimeoutRef.current = setTimeout(async () => {
      setIsSearching(true)
      try {
        const result = await apiClient.get<any>('/search', { q: value.trim() })
        setSearchResults(result?.data?.results ?? result?.results ?? null)
      } catch {
        setSearchResults(null)
      } finally {
        setIsSearching(false)
      }
    }, 500)
  }

  function handleSearchBlur() {
    setTimeout(() => {
      setSearchResults(null)
    }, 200)
  }

  function handleSearchKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Escape') {
      setSearchResults(null)
      setSearchValue('')
    }
  }

  function handleNotifClick(n: any) {
    if (!n.isRead) {
      markReadMutation.mutate({ ids: [n.id] })
    }
    setNotifOpen(false)
    navigate(notificationLink(n))
  }

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  return (
    <header className="flex items-center justify-between h-14 px-4 md:px-6 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 flex-shrink-0">
      {/* Left: Hamburger (mobile) + Breadcrumb */}
      <div className="flex items-center gap-2 min-w-0">
        {/* Mobile hamburger */}
        <button
          onClick={toggleMobileSidebar}
          className="flex md:hidden p-2.5 rounded-lg text-slate-500 hover:bg-slate-100 transition-colors flex-shrink-0 min-w-[44px] min-h-[44px] items-center justify-center"
          aria-label="Apri menu"
        >
          <Menu className="w-5 h-5" />
        </button>

        {/* Breadcrumb */}
        <nav className="flex items-center gap-1 text-sm min-w-0">
          {breadcrumbs.map((crumb, index) => (
            <div key={index} className="flex items-center gap-1 min-w-0">
              {index > 0 && (
                <ChevronRight className="w-3.5 h-3.5 text-slate-300 flex-shrink-0" />
              )}
              {crumb.href ? (
                <Link
                  to={crumb.href}
                  className="text-slate-400 hover:text-slate-600 transition-colors text-xs truncate hidden sm:block"
                >
                  {crumb.label}
                </Link>
              ) : (
                <span className="font-medium text-slate-700 truncate text-xs">
                  {crumb.label}
                </span>
              )}
            </div>
          ))}
        </nav>
      </div>

      {/* Right area: search + notifications + user */}
      <div className="flex items-center gap-2 md:gap-3 flex-shrink-0">
        {/* Search input (hidden on smallest screens, e ricerca globale su istanza non completa) */}
        {instanceComplete && (
        <div className="relative hidden sm:flex items-center">
          <Search className="absolute left-2.5 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
          <input
            type="text"
            placeholder="Cerca... (⌘K)"
            value={searchValue}
            onChange={(e) => handleSearchChange(e.target.value)}
            onBlur={handleSearchBlur}
            onKeyDown={handleSearchKeyDown}
            className={cn(
              'pl-8 pr-3 py-1.5 text-sm bg-slate-50 border border-slate-200 rounded-lg',
              'w-40 md:w-48 focus:w-56 md:focus:w-64 transition-all duration-200',
              'focus:outline-none focus:bg-white focus:border-slate-300',
              'placeholder:text-slate-400'
            )}
          />
          {isSearching && (
            <div className="absolute right-2.5 w-3.5 h-3.5 border-2 border-slate-300 border-t-primary-600 rounded-full animate-spin" />
          )}
          {searchResults && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-slate-800 rounded-2xl shadow-dropdown border border-slate-200 dark:border-slate-700 z-50 max-h-80 overflow-y-auto min-w-[280px]">
              {Object.entries(searchResults).map(([type, items]) => {
                if (!items || items.length === 0) return null
                const typeLabels: Record<string, string> = {
                  clients: 'Clienti',
                  projects: 'Progetti',
                  tasks: 'Task',
                  campaigns: 'Campagne',
                  events: 'Eventi',
                }
                return (
                  <div key={type}>
                    <div className="px-3 py-1.5 bg-slate-50 text-[10px] font-semibold text-slate-400 uppercase tracking-wide">
                      {typeLabels[type] ?? type}
                    </div>
                    {items.map((item: any) => (
                      <Link
                        key={item.id}
                        to={`/${type}/${item.id}`}
                        className="block px-3 py-2 hover:bg-slate-50 text-sm text-slate-700 truncate"
                        onClick={() => {
                          setSearchResults(null)
                          setSearchValue('')
                        }}
                      >
                        {item.name ?? item.title ?? item.id}
                      </Link>
                    ))}
                  </div>
                )
              })}
              {Object.values(searchResults).every((items) => !items || items.length === 0) && (
                <div className="px-3 py-4 text-center text-sm text-slate-400">Nessun risultato</div>
              )}
            </div>
          )}
        </div>
        )}

        {/* Notifications bell with dropdown panel */}
        <div className="relative">
          <button
            onClick={() => {
              setNotifOpen((o) => {
                if (!o) {
                  setBadgeDismissed(true)
                  sessionStorage.setItem('notif-badge-dismissed', 'true')
                }
                return !o
              })
            }}
            className="relative p-2 rounded-lg text-slate-500 hover:bg-slate-100 transition-colors"
            aria-label={`${notificationCount} notifiche`}
          >
            <Bell className="w-4 h-4" />
            {notificationCount > 0 && !badgeDismissed && (
              <span className="absolute top-1 right-1 w-4 h-4 bg-primary-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center leading-none tabular-nums">
                {notificationCount > 9 ? '9+' : notificationCount}
              </span>
            )}
          </button>

          {notifOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setNotifOpen(false)} />
              <div className="fixed sm:absolute left-2 right-2 sm:left-auto sm:right-0 top-14 sm:top-full sm:mt-1 sm:w-96 bg-white dark:bg-slate-800 rounded-2xl shadow-dropdown border border-slate-200 dark:border-slate-700 z-50 animate-fade-in">
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-semibold text-slate-900">Notifiche</h3>
                    {notificationCount > 0 && (
                      <span className="px-1.5 py-0.5 bg-primary-50 text-primary-600 text-[10px] font-bold rounded-full tabular-nums">
                        {notificationCount}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {notificationCount > 0 && (
                      <button
                        onClick={() => markReadMutation.mutate({ all: true })}
                        disabled={markReadMutation.isPending}
                        className="text-xs text-primary-600 hover:text-primary-700 font-medium disabled:opacity-50 transition-colors"
                      >
                        Segna tutte come lette
                      </button>
                    )}
                  </div>
                </div>

                {/* Notifications list */}
                <div className="max-h-[50vh] sm:max-h-[360px] overflow-y-auto divide-y divide-slate-200">
                  {notifications.length === 0 ? (
                    <div className="py-10 text-center">
                      <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <BellOff className="w-5 h-5 text-slate-300" />
                      </div>
                      <p className="text-sm font-medium text-slate-500">Nessuna notifica</p>
                      <p className="text-xs text-slate-400 mt-1">Sei in pari con tutto.</p>
                    </div>
                  ) : (
                    notifications.map((n: any) => (
                      <div
                        key={n.id}
                        className={cn(
                          'px-4 py-3 hover:bg-slate-50 cursor-pointer transition-colors',
                          !n.isRead && 'bg-primary-50/40',
                          notifBorderLeft(n.type, n.title)
                        )}
                        onClick={() => handleNotifClick(n)}
                      >
                        <div className="flex items-start gap-3">
                          {/* Type icon */}
                          <div className={cn(
                            'flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center mt-0.5',
                            notifIconBg(n.type, n.title)
                          )}>
                            <NotificationIcon type={n.type} />
                          </div>

                          <div className="min-w-0 flex-1">
                            <div className="flex items-start justify-between gap-1">
                              <p className="text-xs font-semibold text-slate-800 leading-snug min-w-0 break-words">
                                {n.title}
                              </p>
                              {!n.isRead && (
                                <span className={`w-2 h-2 rounded-full flex-shrink-0 mt-1 ${notifDotColor(n.type, n.title)}`} />
                              )}
                            </div>
                            {n.message && (
                              <p className="text-xs text-slate-500 mt-0.5 line-clamp-2 leading-snug">
                                {n.message}
                              </p>
                            )}
                            <p className="text-[10px] text-slate-400 mt-1">
                              {timeAgo(n.createdAt)}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                {/* Footer */}
                <div className="px-4 py-2.5 border-t border-slate-200">
                  <Link
                    to="/notifications"
                    onClick={() => setNotifOpen(false)}
                    className="block text-center text-xs text-primary-600 hover:text-primary-700 font-medium transition-colors"
                  >
                    Vedi tutte le notifiche
                  </Link>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Activity feed button — nascosto ai guest */}
        <div className="relative" style={{ display: user?.isGuest ? 'none' : undefined }}>
          <button
            onClick={() => { setActivityOpen((o) => !o); setActivityPage(1) }}
            className="relative p-2 rounded-lg text-slate-500 hover:bg-slate-100 transition-colors"
            aria-label="Attività recenti"
          >
            <Activity className="w-4 h-4" />
          </button>

          {activityOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setActivityOpen(false)} />
              <div className="fixed sm:absolute left-2 right-2 sm:left-auto sm:right-0 top-14 sm:top-full sm:mt-1 sm:w-[28rem] bg-white dark:bg-slate-800 rounded-2xl shadow-dropdown border border-slate-200 dark:border-slate-700 z-50 animate-fade-in">
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
                  <div className="flex items-center gap-2">
                    <Activity className="w-4 h-4 text-primary-600" />
                    <h3 className="text-sm font-semibold text-slate-900">Attività Recenti</h3>
                  </div>
                  <span className="text-[10px] text-slate-400">Storico 6 mesi</span>
                </div>

                {/* Activity list */}
                <div className="max-h-[50vh] sm:max-h-[420px] overflow-y-auto divide-y divide-slate-200">
                  {activityLoading ? (
                    <div className="py-10 flex items-center justify-center">
                      <Loader2 className="w-5 h-5 text-slate-300 animate-spin" />
                    </div>
                  ) : activityItems.length === 0 ? (
                    <div className="py-10 text-center">
                      <p className="text-sm text-slate-400">Nessuna attività registrata</p>
                    </div>
                  ) : (
                    activityItems.map((item: any) => {
                      const { label: aLabel, color: aColor } = actionLabel(item.action)
                      const eLabel = entityLabel(item.entity)
                      const route = entityRoute(item.entity, item.entityId)
                      const isExpanded = expandedActivityId === item.id
                      const changes = formatChanges(item.changes)
                      return (
                        <div key={item.id} className="hover:bg-slate-50 transition-colors">
                          <div
                            className="px-4 py-2.5 cursor-pointer"
                            onClick={() => setExpandedActivityId(isExpanded ? null : item.id)}
                          >
                            <div className="flex items-start gap-2.5">
                              {/* Avatar */}
                              <div className="flex-shrink-0 w-7 h-7 bg-primary-50 text-primary-600 rounded-full flex items-center justify-center mt-0.5">
                                <span className="text-[10px] font-bold">
                                  {(item.actor ?? 'S').charAt(0).toUpperCase()}
                                </span>
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="text-xs text-slate-700 leading-snug">
                                  <span className="font-semibold">{item.actor}</span>{' '}
                                  <span className={aColor}>{aLabel}</span>{' '}
                                  <span className="text-slate-500">{eLabel}</span>
                                  {item.entityName ? (
                                    <>{' '}<span className="font-medium text-slate-800">"{item.entityName}"</span></>
                                  ) : null}
                                </p>
                                <p className="text-[10px] text-slate-400 mt-0.5">
                                  {timeAgo(item.timestamp)}
                                </p>
                              </div>
                              {/* Expand indicator */}
                              {changes.length > 0 && (
                                <ChevronDown className={cn(
                                  'w-3.5 h-3.5 text-slate-300 flex-shrink-0 mt-1 transition-transform',
                                  isExpanded && 'rotate-180'
                                )} />
                              )}
                            </div>
                          </div>

                          {/* Expanded changes detail */}
                          {isExpanded && (
                            <div className="px-4 pb-2.5 pl-[3.25rem]">
                              {changes.length > 0 ? (
                                <div className="bg-slate-50 rounded-lg p-2 space-y-1">
                                  {changes.map((c, ci) => (
                                    <div key={ci} className="flex items-baseline gap-2 text-[11px]">
                                      <span className="text-slate-400 font-medium min-w-[5rem] flex-shrink-0">{c.key}:</span>
                                      <span className="text-slate-700 break-all">{c.value}</span>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-[11px] text-slate-400 italic">Nessun dettaglio disponibile</p>
                              )}
                              {route && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    setActivityOpen(false)
                                    navigate(route)
                                  }}
                                  className="mt-1.5 text-[11px] text-primary-600 hover:text-primary-700 font-medium transition-colors"
                                >
                                  Vai a {eLabel} →
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      )
                    })
                  )}
                </div>

                {/* Pagination footer */}
                {activityPagination && activityPagination.totalPages > 1 && (
                  <div className="flex items-center justify-between px-4 py-2 border-t border-slate-200">
                    <button
                      onClick={() => setActivityPage((p) => Math.max(1, p - 1))}
                      disabled={!activityPagination.hasPrev}
                      className="text-xs text-primary-600 hover:text-primary-700 font-medium disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    >
                      ← Precedente
                    </button>
                    <span className="text-[10px] text-slate-400 tabular-nums">
                      {activityPagination.page} / {activityPagination.totalPages}
                    </span>
                    <button
                      onClick={() => setActivityPage((p) => p + 1)}
                      disabled={!activityPagination.hasNext}
                      className="text-xs text-primary-600 hover:text-primary-700 font-medium disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    >
                      Successiva →
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Help button — nascosto ai guest */}
        <div className="relative" style={{ display: user?.isGuest ? 'none' : undefined }}>
          <button
            onClick={() => setHelpOpen((o) => !o)}
            className="relative p-2 rounded-lg text-slate-500 hover:bg-slate-100 transition-colors"
            aria-label="Aiuto pagina"
          >
            <HelpCircle className="w-4 h-4" />
          </button>

          {helpOpen && (() => {
            const help = getHelpForPath(location.pathname)
            return (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setHelpOpen(false)} />
                <div className="fixed sm:absolute left-2 right-2 sm:left-auto sm:right-0 top-14 sm:top-full sm:mt-1 sm:w-96 bg-white dark:bg-slate-800 rounded-2xl shadow-dropdown border border-slate-200 dark:border-slate-700 z-50 animate-fade-in">
                  <div className="px-4 py-3 border-b border-slate-200">
                    <div className="flex items-center gap-2">
                      <HelpCircle className="w-4 h-4 text-primary-600" />
                      <h3 className="text-sm font-semibold text-slate-900">{help.title}</h3>
                    </div>
                  </div>
                  <div className="max-h-[400px] overflow-y-auto px-4 py-3 space-y-3">
                    {help.sections.map((s, i) => (
                      <div key={i}>
                        <h4 className="text-xs font-semibold text-slate-700 mb-0.5">{s.heading}</h4>
                        <p className="text-xs text-slate-500 leading-relaxed">{s.text}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )
          })()}
        </div>

        {/* User dropdown */}
        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <button
              className={cn(
                'flex items-center gap-2 px-2 py-1.5 rounded-lg',
                'hover:bg-slate-100 transition-colors',
                'text-sm font-medium text-slate-700'
              )}
            >
              <div className="w-6 h-6 bg-primary-600 rounded-full flex items-center justify-center">
                <span className="text-white text-[11px] font-semibold">{userInitials}</span>
              </div>
              <span className="hidden md:block text-sm">
                {user ? user.firstName : 'Utente'}
              </span>
              <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
            </button>
          </DropdownMenu.Trigger>

          <DropdownMenu.Portal>
            <DropdownMenu.Content
              className={cn(
                'min-w-48 bg-white dark:bg-slate-800 rounded-2xl shadow-dropdown border border-slate-200 dark:border-slate-700',
                'p-1 z-50 animate-fade-in'
              )}
              align="end"
              sideOffset={4}
            >
              <div className="px-3 py-2 border-b border-slate-200 mb-1">
                <p className="text-sm font-medium text-slate-900">
                  {user ? `${user.firstName} ${user.lastName}` : 'Utente'}
                </p>
                <p className="text-xs text-slate-400">{user?.email ?? ''}</p>
              </div>

              <DropdownMenu.Item
                onSelect={() => navigate('/settings')}
                className="flex items-center gap-2 px-3 py-2 text-sm text-slate-700 rounded-lg cursor-pointer hover:bg-slate-50 outline-none"
              >
                <User className="w-3.5 h-3.5 text-slate-400" />
                Profilo
              </DropdownMenu.Item>

              <DropdownMenu.Item
                onSelect={() => navigate('/settings')}
                className="flex items-center gap-2 px-3 py-2 text-sm text-slate-700 rounded-lg cursor-pointer hover:bg-slate-50 outline-none"
              >
                <Settings className="w-3.5 h-3.5 text-slate-400" />
                Impostazioni
              </DropdownMenu.Item>

              <DropdownMenu.Separator className="my-1 border-t border-slate-200" />

              <DropdownMenu.Item
                onSelect={handleLogout}
                className="flex items-center gap-2 px-3 py-2 text-sm text-danger-600 rounded-lg cursor-pointer hover:bg-danger-50 outline-none"
              >
                <LogOut className="w-3.5 h-3.5" />
                Logout
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      </div>
    </header>
  )
}
